import { apiClient } from '@/lib/api-client';
import type {
  Person,
  PurposeChoice,
  NoteType,
  ReportInputWave2,
  Report,
  ReportStatus,
  ReportSummary,
} from './types';

export type Outcome<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly status: number; readonly code: string };

export async function call<T>(
  run: () => Promise<{ data?: unknown; error?: unknown; response: Response }>,
): Promise<Outcome<T>> {
  try {
    const { data, error, response } = await run();
    if (response.ok) return { ok: true, value: data as T };
    const code =
      typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';
    return { ok: false, status: response.status, code };
  } catch {
    return { ok: false, status: 0, code: '' };
  }
}

export type ReportsQuery = {
  readonly period?: string;
  readonly status?: readonly ReportStatus[];
  readonly owner?: string;
  readonly q?: string;
  readonly purpose?: string;
  readonly offset?: number;
  readonly limit?: number;
  readonly sort?: 'period' | 'number' | 'title' | 'total' | 'status';
  readonly direction?: 'asc' | 'desc';
  readonly cursor?: string;
};

export type ReportsPage = {
  total: number;
  items: ReportSummary[];
  nextCursor: string | null;
  owners: Person[];
  can: { create?: boolean; filterByOwner?: boolean };
};

export function listReports(query: ReportsQuery): Promise<Outcome<ReportsPage>> {
  const { status, ...rest } = query;
  const clean = { ...rest, ...(status === undefined ? {} : { status: [...status] }) };
  return call<ReportsPage>(() =>
    apiClient.GET('/v1/expenses/reports', { params: { query: clean } }),
  );
}

export function getReport(reportId: string): Promise<Outcome<Report>> {
  return call<Report>(() =>
    apiClient.GET('/v1/expenses/reports/{reportId}', { params: { path: { reportId } } }),
  );
}

/** Opens a draft with its purpose (wave 2 R-a); the generated R2 body does not know the new fields. */
export function createReport(input: ReportInputWave2): Promise<Outcome<Report>> {
  return call<Report>(() => apiClient.POST('/v1/expenses/reports', { body: input as never }));
}

/** The purposes the person may use; `ownerUserId` is Gestione filing for someone. */
export async function listPurposes(ownerUserId?: string): Promise<Outcome<PurposeChoice[]>> {
  const query = ownerUserId ? { ownerUserId } : {};
  const out = await call<{ items: PurposeChoice[] }>(() =>
    apiClient.GET('/v1/expenses/purposes', { params: { query } }),
  );
  return out.ok ? { ok: true, value: out.value.items } : out;
}

/** The categories each note type accepts (code only; the labels come from the shared constant). */
export async function listNoteTypes(): Promise<Outcome<Record<NoteType, string[]>>> {
  const out = await call<{ items: { noteType: NoteType; categories: { code: string }[] }[] }>(() =>
    apiClient.GET('/v1/expenses/note-types'),
  );
  if (!out.ok) return out;
  const value: Record<NoteType, string[]> = { travel: [], reimbursement: [] };
  for (const item of out.value.items) value[item.noteType] = item.categories.map((c) => c.code);
  return { ok: true, value };
}

export function deleteReport(reportId: string): Promise<Outcome<unknown>> {
  return call<unknown>(() =>
    apiClient.DELETE('/v1/expenses/reports/{reportId}', { params: { path: { reportId } } }),
  );
}

export function setReportExpenses(
  reportId: string,
  version: string,
  expenseIds: readonly string[],
): Promise<Outcome<Report>> {
  return call<Report>(() =>
    apiClient.PUT('/v1/expenses/reports/{reportId}/expenses', {
      params: { path: { reportId }, header: { 'If-Match': version } },
      body: { expenseIds: [...expenseIds] },
    }),
  );
}

export function submitReport(reportId: string, version: string): Promise<Outcome<Report>> {
  return call<Report>(() =>
    apiClient.POST('/v1/expenses/reports/{reportId}/submit', {
      params: { path: { reportId }, header: { 'If-Match': version } },
    }),
  );
}

/** One expense, as the picker needs it (release 1's list row). */
export type PickerExpense = {
  id: string;
  date: string;
  supplierName?: string | null;
  category: string;
  total: string;
  description?: string | null;
};

/** The person's expenses of a month that belong to no report (`unreported=true`, release 1). */
export async function listUnreported(
  from: string,
  to: string,
  owner?: string,
): Promise<Outcome<PickerExpense[]>> {
  const query = { from, to, unreported: true, limit: 100, ...(owner ? { owner } : {}) };
  const out = await call<{ items: PickerExpense[] }>(() =>
    apiClient.GET('/v1/expenses', { params: { query: query as never } }),
  );
  if (!out.ok) return out;
  return { ok: true, value: out.value.items };
}

/** How the note's PDF comes back: shown in the browser (`inline`) or saved (`attachment`). */
export type PdfDisposition = 'inline' | 'attachment';

/**
 * The note's PDF (R-d), made by the server at the request's time. Through the typed client, so the
 * bearer token and the X-Active-* headers go with it; the caller turns the blob into a tab or a file.
 */
export function getReportPdf(
  reportId: string,
  disposition: PdfDisposition,
): Promise<Outcome<Blob>> {
  return call<Blob>(() =>
    apiClient.GET('/v1/expenses/reports/{reportId}/pdf', {
      params: { path: { reportId }, query: { disposition } },
      parseAs: 'blob',
    }),
  );
}

/** Updates a draft using the version the person opened. */
export function updateReport(
  reportId: string,
  version: string,
  input: ReportInputWave2,
): Promise<Outcome<Report>> {
  return call<Report>(() =>
    apiClient.PATCH('/v1/expenses/reports/{reportId}', {
      params: { path: { reportId }, header: { 'If-Match': version } },
      body: input as never,
    }),
  );
}
